<template>
  <div class="replay-screen cb-page cb-page--fixed">
    <PageHeader title="Повтор проигрыша" :progress="progress" />

    <GlassCard class="replay-screen__card" padding="none" radius="xl">
      <div class="replay-screen__inner">
        <CornerFrame fill border glow class="replay-screen__photo" :radius="24" :corner-size="40" :inset="22">
          <img
            v-if="photo"
            :src="photo"
            :alt="`Кадр нарушения: ${loserName}`"
            class="replay-screen__img"
            :class="{ 'replay-screen__img--loaded': loaded }"
            draggable="false"
            @load="onLoad"
            @error="skip"
          />
          <template #overlay>
            <div v-if="!loaded" class="replay-screen__loading" aria-hidden="true">
              <span class="cb-spinner replay-screen__spinner"></span>
            </div>
            <span v-if="loaded" class="replay-screen__badge">
              <AppIcon :name="mode === 'MOUTH' ? 'mouth' : 'eye'" :size="16" />
              {{ violationLabel }}
            </span>
          </template>
        </CornerFrame>

        <div class="replay-screen__caption">
          <p class="replay-screen__title">Повтор</p>
          <p class="replay-screen__subtitle cb-ellipsis">{{ subtitle }}</p>
        </div>

        <div class="replay-screen__actions">
          <AppButton block @click="skip">Пропустить</AppButton>
        </div>
      </div>
    </GlassCard>
  </div>
</template>

<script setup>
/**
 * ReplayScreen (/replay) — макет 7 «Повтор проигрыша»: кадр нарушения (looserImgLink) в рамке
 * с уголками, подпись «Повтор», «Пропустить». Автопереход через 8 c (линия прогресса в шапке).
 * Победитель → /reward, проигравший → «/». Нет фото или оно не загрузилось → сразу дальше.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import CornerFrame from "@/components/ui/CornerFrame.vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import { useGameStore } from "@/stores/game";
import { useGameOutcome } from "./game/useGameOutcome";

const AUTO_MS = 8000;
const LOAD_TIMEOUT_MS = 7000;

const router = useRouter();
const game = useGameStore();
const { isWinner, looserImgLink, loserName, afterReplayPath } = useGameOutcome();

const photo = looserImgLink.value;
const mode = game.mode === "MOUTH" ? "MOUTH" : "BLINK";
const loaded = ref(false);
const progress = ref(0);

const violationLabel = mode === "MOUTH" ? "Закрытый рот" : "Моргание";
const subtitle = computed(() => (isWinner.value ? `Проиграл соперник: ${loserName.value}` : `Кадр нарушения: ${loserName.value}`));

let leaving = false;
let loadTimer = null;
let progressTimer = null;
let startedAt = 0;

function skip() {
  if (leaving) return;
  leaving = true;
  clearTimeout(loadTimer);
  clearInterval(progressTimer);
  router.replace(afterReplayPath.value);
}

function onLoad() {
  if (loaded.value || leaving) return;
  loaded.value = true;
  clearTimeout(loadTimer);
  startedAt = Date.now();
  progressTimer = setInterval(() => {
    progress.value = Math.min(1, (Date.now() - startedAt) / AUTO_MS);
    if (progress.value >= 1) skip();
  }, 100);
}

onMounted(() => {
  if (!photo) {
    skip();
    return;
  }
  loadTimer = setTimeout(skip, LOAD_TIMEOUT_MS);
});

onBeforeUnmount(() => {
  clearTimeout(loadTimer);
  clearInterval(progressTimer);
});
</script>

<style scoped>
.replay-screen {
  gap: 10px;
}

.replay-screen__card {
  flex: 1 1 auto;
  min-height: 0;
}

.replay-screen__inner {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  padding: 0 0 16px;
}

.replay-screen__photo {
  flex: 1 1 auto;
  min-height: 0;
}

.replay-screen__img {
  opacity: 0;
  transition: opacity var(--cb-dur-slow) var(--cb-ease);
  -webkit-user-drag: none;
}

.replay-screen__img--loaded {
  opacity: 1;
  animation: replay-zoom 8s ease-out both;
}

.replay-screen__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.replay-screen__spinner {
  font-size: 38px;
}

.replay-screen__badge {
  position: absolute;
  top: 16px;
  left: 50%;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transform: translateX(-50%);
  padding: 6px 12px;
  border-radius: var(--cb-radius-pill);
  background: rgba(11, 14, 10, 0.7);
  border: 1px solid var(--cb-lime-border);
  color: var(--cb-lime);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.replay-screen__caption {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0 16px;
  text-align: center;
}

.replay-screen__title {
  font-size: 26px;
  font-weight: 700;
}

.replay-screen__subtitle {
  max-width: 100%;
  color: var(--cb-text-2);
  font-size: 14px;
}

.replay-screen__actions {
  padding: 0 16px;
}

@keyframes replay-zoom {
  from {
    transform: scale(1.06);
  }
  to {
    transform: scale(1);
  }
}
</style>
